import React, { useState } from 'react';
import { ArrowLeft, Check, X, ExternalLink } from 'lucide-react';
import { getAdapter } from '../../lib/adapters';
import type { MemberSubmission, SubmissionRelationship } from '../../lib/adapters';
import { MemberPicker } from '../Forms/MemberPicker';
import { useFamilyStore } from '../../store/familyStore';
import { pushRoute } from '../../utils/routing';

interface SubmissionReviewProps {
  submission: MemberSubmission;
  onBack: () => void;
  /** Called with the updated submission after approve/reject */
  onResolved: (updated: MemberSubmission) => void;
}

type Gender = 'male' | 'female' | 'other';
type MaritalStatus = 'single' | 'married' | 'divorced' | 'widowed';
type RelType = 'child' | 'spouse' | 'sibling' | 'parent';

const FIELD_LABELS: Record<string, string> = {
  nickname: 'Nickname',
  birthDate: 'Birth Date',
  deathDate: 'Death Date',
  birthPlace: 'Birth Place',
  currentLocation: 'Current Location',
  profession: 'Profession',
  education: 'Education',
  notes: 'Notes',
};

export const SubmissionReview: React.FC<SubmissionReviewProps> = ({
  submission,
  onBack,
  onResolved,
}) => {
  const { members, fetchMembers } = useFamilyStore();
  const [form, setForm] = useState<MemberSubmission>(submission);
  const [relType, setRelType] = useState<RelType | ''>(submission.relationship?.type ?? '');
  const [relatedMemberId, setRelatedMemberId] = useState<string | null>(
    submission.relationship?.relatedMemberId ?? null,
  );
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [approvedMemberId, setApprovedMemberId] = useState<string | null>(null);

  const treeId = submission.treeId;

  React.useEffect(() => {
    fetchMembers(treeId);
  }, [treeId, fetchMembers]);

  const set = (patch: Partial<MemberSubmission>) => setForm(f => ({ ...f, ...patch }));

  const saveEdits = async (): Promise<MemberSubmission> => {
    const relationship: SubmissionRelationship | null = relType && relatedMemberId
      ? { type: relType, relatedMemberId }
      : null;
    return getAdapter().updateSubmission(form.id, {
      name: form.name,
      nickname: form.nickname,
      birthDate: form.birthDate,
      deathDate: form.deathDate,
      birthPlace: form.birthPlace,
      currentLocation: form.currentLocation,
      profession: form.profession,
      education: form.education,
      gender: form.gender,
      maritalStatus: form.maritalStatus,
      isAlive: form.isAlive,
      notes: form.notes,
      relationship,
    });
  };

  const handleApprove = async () => {
    setError(null);
    if (form.name.trim().length < 2) {
      setError('Name must be at least 2 characters.');
      return;
    }
    if (relType && !relatedMemberId) {
      setError('You selected a relationship type but no related member. Pick one or clear the type.');
      return;
    }
    setBusy('approve');
    try {
      await saveEdits();
      const { member } = await getAdapter().approveSubmission(form.id);
      setApprovedMemberId(member.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve submission');
    } finally {
      setBusy(null);
    }
  };

  const handleReject = async () => {
    setError(null);
    if (!confirm('Reject this submission? The data will not be added to the tree.')) return;
    setBusy('reject');
    try {
      const updated = await getAdapter().rejectSubmission(form.id);
      onResolved(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject submission');
    } finally {
      setBusy(null);
    }
  };

  const relLabel = relType ? `${relType} of ${members.find(m => m.id === relatedMemberId)?.name ?? '…'}` : null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to queue
        </button>
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Pending review</span>
      </div>

      {approvedMemberId ? (
        <div className="p-10 text-center">
          <p className="text-2xl font-bold text-gray-900 mb-2">Submission approved!</p>
          <p className="text-gray-500 mb-6">
            <span className="font-medium text-gray-900">{form.name}</span> has been added to the family tree.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => pushRoute(`/family-tree/${treeId}?member=${approvedMemberId}`)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-700 text-white font-medium rounded-lg hover:bg-green-800 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              View in tree
            </button>
            <button
              onClick={onBack}
              className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Back to queue
            </button>
          </div>
        </div>
      ) : (
        <div className="p-6 space-y-5">
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{error}</p>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={e => set({ name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              <select
                value={form.gender}
                onChange={e => set({ gender: e.target.value as Gender })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-600"
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Marital Status</label>
              <select
                value={form.maritalStatus}
                onChange={e => set({ maritalStatus: e.target.value as MaritalStatus })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-600"
              >
                <option value="single">Single</option>
                <option value="married">Married</option>
                <option value="divorced">Divorced</option>
                <option value="widowed">Widowed</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isAlive}
                onChange={e => set({ isAlive: e.target.checked })}
                className="w-4 h-4 text-green-600 rounded"
              />
              Currently alive
            </label>
            {!form.isAlive && (
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Death Date</label>
                <input
                  type="date"
                  value={form.deathDate ?? ''}
                  onChange={e => set({ deathDate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(['nickname', 'birthDate', 'birthPlace', 'currentLocation', 'profession', 'education'] as const).map(field => (
              <div key={field}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{FIELD_LABELS[field]}</label>
                <input
                  type={field === 'birthDate' ? 'date' : 'text'}
                  value={form[field] ?? ''}
                  onChange={e => set({ [field]: e.target.value } as Partial<MemberSubmission>)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
                />
              </div>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes ?? ''}
              onChange={e => set({ notes: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
            />
          </div>

          {/* Relationship */}
          <div className="border-t border-gray-100 pt-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Relationship to the tree</h3>
            <p className="text-xs text-gray-500 mb-4">
              {relLabel ?? 'No relationship specified.'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                <select
                  value={relType}
                  onChange={e => setRelType(e.target.value as RelType | '')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-600"
                >
                  <option value="">None</option>
                  <option value="child">Child of…</option>
                  <option value="spouse">Spouse of…</option>
                  <option value="sibling">Sibling of…</option>
                  <option value="parent">Parent of…</option>
                </select>
              </div>
              <MemberPicker
                members={members}
                value={relatedMemberId}
                onChange={setRelatedMemberId}
                label="Related to"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              onClick={handleReject}
              disabled={busy !== null}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-60"
            >
              <X className="w-4 h-4" />
              {busy === 'reject' ? 'Rejecting…' : 'Reject'}
            </button>
            <button
              onClick={handleApprove}
              disabled={busy !== null}
              className="inline-flex items-center gap-2 px-5 py-2 bg-green-700 text-white text-sm font-semibold rounded-lg hover:bg-green-800 transition-colors disabled:opacity-60"
            >
              <Check className="w-4 h-4" />
              {busy === 'approve' ? 'Adding to tree…' : 'Approve & Add to Tree'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
